import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';
import { DEFAULT_HYPOTHESIS_CREATIVES_VIEW_STATE } from '../types/hypothesis';
import { PartnershipCreationMode, PartnershipIdentityToShow } from '../types';
import {
  collectAdSettingsFlowsToValidate,
  toAdSetFlowMatch,
} from '../utils/flowAdSetCount';
import {
  validateAdSettingsFieldsAtIndex,
  validateAdSettingsForFlowsAtIndex,
} from './adSettingsValidation';
import { selectedAdSetsFlowsSchema, validateAdSetsStep } from './validateSelectedAdSetsFlows';

const schema = z
  .object({
    currentStep: z.number().default(0).optional(),
    app: z.string().nonempty('Application is required'),
    platform: z.string().optional(),
    marketingProcess: z.literal('scaling_eg'),
    adType: z.string().optional(),
    adAccounts: z.array(z.string()).optional(),
    selectedCampaigns: z.array(z.any()).optional(),
    selectedCampaignsOnly: z.boolean(),
    selectedAdSets: z.array(z.any()).optional(),
    selectedCampaignsBudgets: z.record(
      z.string(),
      z.string().refine((val) => val.trim() !== '', {
        message: 'Cannot be empty',
      }),
    ),
    selectedAdSetsBudgets: z.record(z.string(), z.string()).default({}),
    selectedAdSetsDuplicatesCount: z.record(z.string(), z.string()).default({}),
    selectedAdSetsStartTimes: z.record(z.string(), z.string()).default({}),
    selectedAdSetsFlows: selectedAdSetsFlowsSchema,
    selectedCreatives: z.array(z.string()).default([]),
    callToActions: z.string().optional(),
    selectedConversionDomain: z.string().optional(),
    packAdSettings: z.array(z.record(z.unknown())).default([{}]),
    partnershipAdSettingsFields: z.array(z.record(z.unknown())).default([{}]),
    hotjar: z.boolean().optional(),
    conversionDomain: z.string().optional(),
    adSettings: z.string().optional(),
    adLabelSuffix: z.string().optional(),
    partnershipAdsEnabled: z.boolean().default(false),
    partnershipIsPartnerIdentityFirst: z.boolean().optional(),
    partnershipIdentityToShow: z.nativeEnum(PartnershipIdentityToShow).optional(),
    partnershipCreationMode: z.nativeEnum(PartnershipCreationMode).optional(),
    partnershipAdSettings: z
      .array(
        z.object({
          partnershipId: z.string().default(''),
          partnershipTag: z.string().default(''),
          partnershipPartnerPageId: z.string().default(''),
          partnershipAppPageId: z.string().default(''),
          partnershipPartnerInstagramId: z.string().nullable().default(null),
        }),
      )
      .default([]),
    packsWithCreatives: z
      .array(
        z.object({
          tag: z.string().default(''),
          adType: z.string().default(''),
          creatives: z.array(z.string()).default([]),
          filterCreativeByName: z.array(z.string()).default([]),
          visual_touch_ups: z.boolean().default(true),
          isMixedPack: z.boolean().optional(),
        }),
      )
      .default([]),
    activePacksWithCreatives: z.number().nullable().optional(),
    activePackTag: z.string().default(''),
    filterCampaignsByName: z.array(z.string()).default([]),
    filterAdSetsByName: z.array(z.string()).default([]),
    filterCreativesByName: z.array(z.string()).default([]),
    selectedCreativesOnly: z.boolean().default(false),
    testCreativesViewState: z
      .object({
        search: z.string().default(''),
        hypothesis: z.array(z.string()).default([]),
        customer: z.string().nullable().default(null),
        flow: z.array(z.string()).default([]),
        technology: z.array(z.string()).default([]),
        tpcFinishDate: z.string().nullable().default(null),
        status: z.enum(['launched', 'not_launched']).nullable().default(null),
        uploaded: z
          .enum(['last_week', 'last_2_weeks', '1_month', 'all'])
          .default('last_week'),
        groupByHypothesis: z.boolean().default(true),
        unpackedOrderByHypothesis: z.record(z.array(z.string())).default({}),
      })
      .default(DEFAULT_HYPOTHESIS_CREATIVES_VIEW_STATE),
  })
  .superRefine((data, ctx) => {
    const currentStep = data.currentStep;

    if (currentStep === 0 && !data?.app) {
      ctx.addIssue({
        code: 'custom',
        message: 'Application is required',
        path: ['app'],
      });
    }

    if (currentStep === 0 && !data.adType?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: "Can't be empty",
        path: ['adType'],
      });
    }

    if (currentStep === 0 && !data.selectedCreatives.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one creative has to be applied',
        path: ['selectedCreatives'],
      });
    }

    if (currentStep === 1 && !data?.adAccounts?.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'AdAccounts is required at this step',
        path: ['adAccounts'],
      });
    }

    if (currentStep === 1 && !data?.platform) {
      ctx.addIssue({
        code: 'custom',
        message: 'Platform is required',
        path: ['platform'],
      });
    }

    if (currentStep === 2 && !data?.selectedCampaigns?.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one campaign must be selected at this step',
        path: ['selectedCampaigns'],
      });
    }

    validateAdSetsStep(data, ctx, {
      validateBudgets: true,
      validateDuplicates: false,
    });

    if (currentStep === 4 && !data?.callToActions) {
      ctx.addIssue({
        code: 'custom',
        message: "Can't be empty",
        path: ['callToActions'],
      });
    }

    if (
      currentStep === 4 &&
      data.platform === 'WEB' &&
      !data?.selectedConversionDomain?.length
    ) {
      ctx.addIssue({
        code: 'custom',
        message: "Can't be empty",
        path: ['selectedConversionDomain'],
      });
    }

    if (currentStep === 4 && !data.partnershipAdsEnabled) {
      const selectedAdSetsForFlowMatch = (data.selectedAdSets ?? []).map(toAdSetFlowMatch);
      const flowsToValidate = collectAdSettingsFlowsToValidate(
        data.packAdSettings[0],
        selectedAdSetsForFlowMatch,
        data.selectedCreatives.length,
      );

      if (flowsToValidate.length > 0) {
        validateAdSettingsForFlowsAtIndex(
          data.packAdSettings[0],
          0,
          ctx,
          'packAdSettings',
          flowsToValidate,
        );
      } else {
        validateAdSettingsFieldsAtIndex(data.packAdSettings[0], 0, ctx, 'packAdSettings');
      }
    }
  });

export type ScalingEgFormData = z.infer<typeof schema>;

export default {
  schema,
  resolver: zodResolver(schema),
} as {
  schema: typeof schema;
  resolver: Resolver<ScalingEgFormData>;
};
